// frontend/src/App.jsx
import React, { useState, useEffect, useMemo, useRef, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import UserProfile from './components/UserProfile';
import ErrorBoundary from './components/ErrorBoundary';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import Sidebar from './components/Sidebar';
import DisabledModuleDialog from './components/DisabledModuleDialog';
import ThemeToggle from './components/ThemeToggle';
import NotificationsBell from './components/NotificationsBell';
import HeaderSearch from './components/HeaderSearch';
import FeedbackWidget from './components/FeedbackWidget';
import { useAuth } from './hooks/useAuth';
import { lazyWithRetry, ChunkLoadingFallback } from './utils/lazyLoad';
import { isSupplierPortalPath } from './utils/routeBoundaries';
import { Bell, Bot, LoaderCircle, Menu } from 'lucide-react';
import { canUseRoute, getRoutePolicy } from './routes/routePolicies';
import { setPermissions } from './utils/permissions';
import { hasPrerenderedPage } from './seo/usePrerenderHandoff';
import './styles/theme.css';
import './app.css';

// Lazy-load all pages with automatic retry support
const LandingPage = lazyWithRetry(() => import('./pages/LandingPage'));
const LoginForm = lazyWithRetry(() => import('./components/LoginForm'));
const loadGlobalAssistant = () => import('./components/GlobalAssistantDock');
const GlobalAssistantDock = lazyWithRetry(loadGlobalAssistant);
const PortalApp = lazyWithRetry(() => import('./portal/PortalApp'));
const SupplierApp = lazyWithRetry(() => import('./pages/supplier/SupplierApp'));
const TenantLoginPage = lazyWithRetry(() => import('./pages/TenantLoginPage'));
const ActivateAccountPage = lazyWithRetry(() => import('./pages/ActivateAccountPage'));
const ResetPasswordPage = lazyWithRetry(() => import('./pages/ResetPasswordPage'));
const LegalPage = lazyWithRetry(() => import('./pages/LegalPage'));
const ErpPage = lazyWithRetry(() => import('./pages/ErpPage'));
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'));
const TasksView = lazyWithRetry(() => import('./pages/TasksView'));
const AnalyticsView = lazyWithRetry(() => import('./pages/AnalyticsView'));
const AIInsights = lazyWithRetry(() => import('./pages/AIInsights'));
const OperationsDashboard = lazyWithRetry(() => import('./pages/OperationsDashboard'));
const OperationsControlCenter = lazyWithRetry(() => import('./pages/OperationsControlCenter'));
const CustomerOrdersView = lazyWithRetry(() => import('./pages/CustomerOrdersView'));
const ErpDataView = lazyWithRetry(() => import('./pages/ErpDataView'));
const InventoryPlanningView = lazyWithRetry(() => import('./pages/InventoryPlanningView'));
const AIRecommendationsView = lazyWithRetry(() => import('./pages/AIRecommendationsView'));
const TaskEscalationsView = lazyWithRetry(() => import('./pages/TaskEscalationsView'));
const SettingsView = lazyWithRetry(() => import('./pages/SettingsView'));
const AIChatView = lazyWithRetry(() => import('./pages/AIChatView'));
const CanvasView = lazyWithRetry(() => import('./pages/CanvasView'));
const ToolsView = lazyWithRetry(() => import('./pages/ToolsView'));
const RFQView = lazyWithRetry(() => import('./pages/RFQView'));
const PRManagementView = lazyWithRetry(() => import('./pages/PRManagementView'));
const FinanceCommandCenterView = lazyWithRetry(() => import('./pages/FinanceCommandCenterView'));
const APAgentView = lazyWithRetry(() => import('./pages/APAgentView'));
const APWorkbenchView = lazyWithRetry(() => import('./pages/APWorkbenchView'));
const FinanceActionLedgerView = lazyWithRetry(() => import('./pages/FinanceActionLedgerView'));
const CashFlowView = lazyWithRetry(() => import('./pages/CashFlowView'));
const CollectionsView = lazyWithRetry(() => import('./pages/CollectionsView'));
const CollectionsWorkbenchView = lazyWithRetry(() => import('./pages/CollectionsWorkbenchView'));
const AnomalyDetectionView = lazyWithRetry(() => import('./pages/AnomalyDetectionView'));
const CustomerSupportView = lazyWithRetry(() => import('./pages/CustomerSupportView'));
const WhatsAppSalesView = lazyWithRetry(() => import('./pages/WhatsAppSalesView'));
const SalesBotView = lazyWithRetry(() => import('./pages/SalesBotView'));
const CommunicationAgentView = lazyWithRetry(() => import('./pages/CommunicationAgentView'));
const WorkflowsView = lazyWithRetry(() => import('./pages/WorkflowsView'));
const DocumentProcessingView = lazyWithRetry(() => import('./pages/DocumentProcessingView'));
const StagedRecordsView = lazyWithRetry(() => import('./pages/StagedRecordsView'));
const GrnReceivingView = lazyWithRetry(() => import('./pages/GrnReceivingView'));
const PresentationAgentView = lazyWithRetry(() => import('./pages/PresentationAgentView'));
const JobSchedulerView = lazyWithRetry(() => import('./pages/JobSchedulerView'));
const UserManagementView = lazyWithRetry(() => import('./pages/UserManagementView'));
const TokenUsageView = lazyWithRetry(() => import('./pages/TokenUsageView'));
const ARCollectionsView = lazyWithRetry(() => import('./pages/ARCollectionsView'));
const ARAnalyticsView = lazyWithRetry(() => import('./pages/ARAnalyticsView'));
const ARDisputesView = lazyWithRetry(() => import('./pages/ARDisputesView'));
const WorkspaceFoldersView = lazyWithRetry(() => import('./pages/WorkspaceFoldersView'));
const FolderView = lazyWithRetry(() => import('./pages/FolderView'));
const TransmittalsInboxView = lazyWithRetry(() => import('./pages/TransmittalsInboxView'));
const RouteTemplatesView = lazyWithRetry(() => import('./pages/RouteTemplatesView'));
const ValidationRulesView = lazyWithRetry(() => import('./pages/ValidationRulesView'));
const CabinetView = lazyWithRetry(() => import('./pages/CabinetView'));
const CaptureSettingsView = lazyWithRetry(() => import('./pages/CaptureSettingsView'));
const CaptureActivityView = lazyWithRetry(() => import('./pages/CaptureActivityView'));
const ErpSettingsView = lazyWithRetry(() => import('./pages/ErpSettingsView'));
const SearchView = lazyWithRetry(() => import('./pages/SearchView'));
const BankRecView = lazyWithRetry(() => import('./pages/BankRecView'));
const ExportsComplianceView = lazyWithRetry(() => import('./pages/ExportsComplianceView'));
const StockCountView = lazyWithRetry(() => import('./pages/StockCountView'));
const ConsignmentView = lazyWithRetry(() => import('./pages/ConsignmentView'));
const TransfersView = lazyWithRetry(() => import('./pages/TransfersView'));
const TransferRequestsView = lazyWithRetry(() => import('./pages/TransferRequestsView'));
const TransferRequestPage = lazyWithRetry(() => import('./pages/TransferRequestPage'));
const TransferNotePage = lazyWithRetry(() => import('./pages/TransferNotePage'));
const StockCardView = lazyWithRetry(() => import('./pages/StockCardView'));
const WarehouseCountsView = lazyWithRetry(() => import('./pages/WarehouseCountsView'));
const PriceListsView = lazyWithRetry(() => import('./pages/PriceListsView'));
const StockReportsView = lazyWithRetry(() => import('./pages/StockReportsView'));
const CustomerCatalogView = lazyWithRetry(() => import('./pages/CustomerCatalogView'));
const EmployeesView = lazyWithRetry(() => import('./pages/EmployeesView'));
const UploadLogView = lazyWithRetry(() => import('./pages/UploadLogView'));
const CountDashboardView = lazyWithRetry(() => import('./pages/CountDashboardView'));
const TransferDashboardView = lazyWithRetry(() => import('./pages/TransferDashboardView'));
const SalesReportsView = lazyWithRetry(() => import('./pages/SalesReportsView'));
const SapVarianceView = lazyWithRetry(() => import('./pages/SapVarianceView'));
const ScannedSalesView = lazyWithRetry(() => import('./pages/ScannedSalesView'));
const RoutesView = lazyWithRetry(() => import('./pages/RoutesView'));
const BatchesView = lazyWithRetry(() => import('./pages/BatchesView'));
const ProductionView = lazyWithRetry(() => import('./pages/ProductionView'));
const MobileCountView = lazyWithRetry(() => import('./pages/MobileCountView'));
const BranchPortal = lazyWithRetry(() => import('./pages/BranchPortal'));
const BranchReview = lazyWithRetry(() => import('./pages/BranchReview'));
// a staff page needs a staff shift, not whatever the office left in the browser
const RequireStaff = ({ children }) => (localStorage.getItem('branchToken') || localStorage.getItem('authToken') ? children : <Navigate to="/branch" replace />);
const MasterDataView = lazyWithRetry(() => import('./pages/MasterDataView'));
const ApprovalsView = lazyWithRetry(() => import('./pages/ApprovalsView'));
const PriceBookView = lazyWithRetry(() => import('./pages/PriceBookView'));
const ReportsView = lazy(() => import('./pages/ReportsView'));
const ProcurementControlView = lazy(() => import('./pages/ProcurementControlView'));
const StockCountShareView = lazyWithRetry(() => import('./pages/StockCountShareView'));
const CashFlowScenariosView = lazyWithRetry(() => import('./pages/CashFlowScenariosView'));
const FinancialForecastView = lazyWithRetry(() => import('./pages/FinancialForecastView'));
const SupplierPaymentsView = lazyWithRetry(() => import('./pages/SupplierPaymentsView'));
const ModulesView = lazyWithRetry(() => import('./pages/ModulesView'));
const TenantManagementView = lazyWithRetry(() => import('./pages/TenantManagementView'));
const PlanUsageView = lazyWithRetry(() => import('./pages/PlanUsageView'));
const TenantOnboardingView = lazyWithRetry(() => import('./pages/TenantOnboardingView'));

const TENANT_HOME_PATH = '/ai-chat';

// File-browser pages manage their own (slim) padding — the shell's 1.5rem wrapper
// padding is removed for these so the content area is maximised. Prefix match, so
// child routes (/transaction-folders/<id>) are covered too.
const FLUSH_MAIN_PATHS = ['/cabinet', '/transaction-folders'];
const isFlushPath = (p) => FLUSH_MAIN_PATHS.some((f) => p === f || p.startsWith(f + '/'));

// Checks if a moduleKey is enabled in the tenant settings loaded by this app.
// Falls back to true so routes still render if settings haven't loaded.
const isModuleAllowed = (moduleKey) => {
  if (!moduleKey) return true;
  const mods = window.__acuon_modules;
  if (!mods) return true;
  return mods[`module_${moduleKey}`] !== false;
};

const isModuleReleased = (moduleKey) => {
  if (!moduleKey) return true;
  const catalog = window.__acuon_module_catalog || {};
  const entry = catalog[moduleKey];
  if (!entry) return true;
  return entry.can_activate !== false;
};

const ProtectedRoute = ({ element: Element, moduleKey, requiredRole }) => {
  const location = useLocation();
  // Auth guard — should not normally be hit (landing page redirects), but
  // prevents direct URL access to authenticated routes.
  // Head office signs in by phone, never by email. An office admin already
  // signed in on the portal who opens a filing page is let through on that
  // same phone session — never sent to the email sign-in, which is not theirs.
  if (!localStorage.getItem('authToken')) {
    try {
      const bu = JSON.parse(localStorage.getItem('branchUser') || '{}');
      const bt = localStorage.getItem('branchToken');
      if (bt && bu.branch_role === 'office_admin') {
        localStorage.setItem('authToken', bt);
        localStorage.setItem('userData', JSON.stringify({ ...bu, from_portal: true }));
        localStorage.setItem('deskScope', JSON.stringify(['/cabinet', '/transaction-folders', '/search', '/document-processing']));
      }
    } catch { /* no portal session to adopt */ }
  }
  const token = localStorage.getItem('authToken');
  if (!token) return <Navigate to="/" replace />;

  // A desk session handed out by a phone sign-in opens the filing pages and
  // nothing else: an office admin needs the cabinet their uploads land in,
  // not the run of the office.
  let scope = null;
  try { scope = JSON.parse(localStorage.getItem('deskScope') || 'null'); } catch { scope = null; }
  if (Array.isArray(scope) && !scope.some((p) => location.pathname.startsWith(p))) {
    return <Navigate to="/branch" replace />;
  }

  let userData = {};
  try {
    userData = JSON.parse(localStorage.getItem('userData') || '{}');
  } catch {
    userData = {};
  }
  const routePolicy = getRoutePolicy(location.pathname);
  const access = canUseRoute(
    {
      ...routePolicy,
      requiredRole: requiredRole || routePolicy.requiredRole,
    },
    userData,
    window.__acuon_modules || null,
  );
  // a platform page typed straight into the address bar: not a page this
  // workspace has, so it goes home rather than explaining who manages it
  if (!access.allowed && access.reason === 'platform') {
    return <Navigate to={TENANT_HOME_PATH} replace />;
  }
  if (!access.allowed && access.reason === 'role') {
    return (
      <div className="loading-spinner" style={{ flexDirection: 'column', gap: 8 }}>
        <p style={{ color: '#475569', fontWeight: 700 }}>This page is managed by your workspace administrators.</p>
      </div>
    );
  }
  if (!access.allowed && access.reason === 'access') {
    return (
      <div className="loading-spinner" style={{ flexDirection: 'column', gap: 8 }}>
        <p style={{ color: '#475569', fontWeight: 700 }}>This page is open to the people your administrator has named.</p>
      </div>
    );
  }

  // Module guard
  const effectiveModuleKey = moduleKey || routePolicy.moduleKey;
  if (effectiveModuleKey && !isModuleReleased(effectiveModuleKey)) {
    return <Navigate to={TENANT_HOME_PATH} replace />;
  }
  if (effectiveModuleKey && !isModuleAllowed(effectiveModuleKey)) {
    return <DisabledModuleDialog moduleKey={effectiveModuleKey} />;
  }

  return (
    <RouteErrorBoundary>
      <Suspense fallback={<ChunkLoadingFallback />}>
        <Element />
      </Suspense>
    </RouteErrorBoundary>
  );
};

const RoutePathSync = ({ onPathChange }) => {
  const { pathname } = useLocation();

  useEffect(() => {
    onPathChange(pathname);
  }, [onPathChange, pathname]);

  return null;
};

const API_URL = import.meta.env.VITE_API_URL ?? '';

function App() {
  const { user, isAuthenticated, isLoading, logout, demoLogin } = useAuth();
  const [systemHealth, setSystemHealth] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [showLogin, setShowLogin] = useState(false);
  const [loginMode, setLoginMode] = useState('login');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState(() => window.location.pathname);
  const [notifBell, setNotifBell] = useState([]);      // persisted notifications from DB
  const knownNotificationIds = useRef(null);
  const [enabledModules, setEnabledModules] = useState(null);
  const [moduleCatalog, setModuleCatalog] = useState(null);

  // Intersect global module flags with role-level allowed modules
  const effectiveModules = useMemo(() => {
    if (!enabledModules || !user) return enabledModules;
    const roleKey = `role_modules_${user.role}`;
    const allowed = enabledModules[roleKey];
    if (!allowed || allowed.includes('*')) return enabledModules;
    const result = { ...enabledModules };
    Object.keys(result).forEach(k => {
      if (k.startsWith('module_')) {
        const modKey = k.replace('module_', '');
        if (!allowed.includes(modKey)) result[k] = false;
      }
    });
    return result;
  }, [enabledModules, user]);
  const [notifUnread, setNotifUnread] = useState(0);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const activeDocumentIntakes = useMemo(
    () => notifBell.filter(n => (
      n.notification_type === 'processing'
      && n.metadata?.processing_state === 'processing'
    )),
    [notifBell],
  );

  useEffect(() => {
    fetchSystemHealth();
  }, []);

  const addNotification = (notification) => {
    setNotifications(prev => [notification, ...prev.slice(0, 4)]);

    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
    }, 10000);
  };

  const fetchSystemHealth = async () => {
    try {
      const apiResponse = await fetch(`${API_URL}/health`);
      if (apiResponse.ok) {
        const health = await apiResponse.json();
        setSystemHealth(health);
        return;
      }
    } catch (error) {
      console.error('Failed to fetch system health:', error);
      setSystemHealth({
        status: 'unhealthy',
        note: 'API health check failed'
      });
    }
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Fetch module enabled/disabled settings and release metadata once on login, and on demand
  const refreshModules = () => {
    const token = localStorage.getItem('authToken') || '';
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    Promise.all([
      fetch(`${API_URL}/api/v1/app-settings/`, { headers }).then(r => r.ok ? r.json() : null),
      fetch(`${API_URL}/api/v1/modules`, { headers }).then(r => r.ok ? r.json() : null),
      // what this person's role may do — the menu and the buttons follow it
      fetch(`${API_URL}/auth/permissions/me`, { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
    ])
      .then(([settingsData, modulesData, mine]) => {
        const perms = Array.isArray(mine?.permissions) ? mine.permissions : null;
        if (perms) setPermissions(perms);
        // app-settings answer only an administrator; everyone else still
        // learns which permission-gated pages are theirs
        if (settingsData || perms) {
          const flags = { ...(settingsData || {}) };
          if (perms) flags.access_whatsapp_sales = perms.includes('whatsapp.open');
          setEnabledModules(flags);
          window.__acuon_modules = flags;
        }

        const catalog = {};
        if (modulesData?.modules) {
          modulesData.modules.forEach((module) => {
            if (module?.key) catalog[module.key] = module;
          });
          setModuleCatalog(catalog);
          window.__acuon_module_catalog = catalog;
        }
      })
      .catch(() => {
        setModuleCatalog(null);
        window.__acuon_module_catalog = {};
      });
  };
  useEffect(() => {
    if (!isAuthenticated) return;
    refreshModules();
    window.addEventListener('acuon:modules-changed', refreshModules);
    return () => window.removeEventListener('acuon:modules-changed', refreshModules);
  }, [isAuthenticated]);

  // Poll persisted notifications frequently enough to surface active email intake.
  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchNotifs = async () => {
      try {
        const token = localStorage.getItem('authToken') || '';
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const [wfRes, docRes] = await Promise.all([
          fetch(`${API_URL}/api/v1/workflows/notifications?limit=20`, { headers }).catch(() => null),
          fetch(`${API_URL}/api/v1/notifications?page=1&page_size=15`, { headers }).catch(() => null),
        ]);

        let wfNotifs = [];
        let docNotifs = [];
        let wfUnreadCount = 0;

        if (wfRes && wfRes.ok) {
          const wfData = await wfRes.json().catch(() => ({}));
          wfNotifs = wfData.notifications || [];
          wfUnreadCount = Number.isFinite(wfData.unread_count) ? wfData.unread_count : wfNotifs.filter(n => !n.is_read).length;
        }

        if (docRes && docRes.ok) {
          const docData = await docRes.json().catch(() => ({}));
          docNotifs = (docData.notifications || []).map(n => ({
            id: `doc-${n.notif_id || n.id}`,
            title: n.title || 'Document Notification',
            message: n.message || n.summary || '',
            notification_type: n.type || 'info',
            source: 'documents',
            action_path: n.link || '/document-processing',
            is_read: !!n.read,
            created_at: n.created_at || new Date().toISOString(),
          }));
        }

        const combinedMap = new Map();
        [...wfNotifs, ...docNotifs].forEach(n => {
          const key = String(n.id);
          if (!combinedMap.has(key)) combinedMap.set(key, n);
        });

        const all = Array.from(combinedMap.values()).sort(
          (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
        );

        const unread = all.filter(n => !n.is_read);
        const currentStates = new Map(all.map(n => [
          n.id,
          `${n.notification_type || ''}:${n.updated_at || n.created_at || ''}`,
        ]));
        const now = Date.now();
        const newAlerts = knownNotificationIds.current === null
          ? unread.filter(n => n.notification_type !== 'processing' && now - new Date(n.created_at).getTime() < 120000)
          : unread.filter(n => (
            n.notification_type !== 'processing'
            && knownNotificationIds.current.get(n.id) !== currentStates.get(n.id)
          ));

        newAlerts.slice(0, 3).forEach(n => addNotification({
          id: `persistent-${n.id}`,
          type: n.notification_type || 'info',
          title: n.title || 'Notification',
          message: n.message || '',
          timestamp: n.created_at || new Date().toISOString(),
        }));
        knownNotificationIds.current = currentStates;
        setNotifBell(all);
        setNotifUnread(Math.max(wfUnreadCount, unread.length));
      } catch { }
    };
    fetchNotifs();
    const id = setInterval(fetchNotifs, 10000);
    return () => clearInterval(id);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) knownNotificationIds.current = null;
  }, [isAuthenticated]);

  const markAllNotifRead = async () => {
    try {
      const token = localStorage.getItem('authToken') || '';
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await Promise.all([
        fetch(`${API_URL}/api/v1/workflows/notifications/mark-read`, { method: 'POST', headers }).catch(() => {}),
        fetch(`${API_URL}/api/v1/notifications/read-all`, { method: 'POST', headers }).catch(() => {}),
      ]);
      setNotifUnread(0);
      setNotifBell(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch { }
  };

  const handleLoginSuccess = (loginData) => {
    if (loginData.access_token && loginData.user) {
      demoLogin(loginData);
    }
    setShowLogin(false);
    fetchSystemHealth();
    const platformAdminUser = loginData.user?.is_platform_admin === true || loginData.user?.role === 'super_admin';
    window.location.href = platformAdminUser ? '/platform-admin' : TENANT_HOME_PATH;
  };

  const handleLogout = () => {
    logout();
  };

  if (isLoading) {
    // A prerendered public page is already on screen; keep it there rather
    // than covering it with a spinner while the session check runs.
    if (hasPrerenderedPage()) return null;
    return (
      <div className="app loading">
        <div className="loading-spinner">
          <div className="spinner large"></div>
          <p>Initializing Acuon...</p>
        </div>
      </div>
    );
  }

  // Read tenant name from user data for display in header
  const tenantName = (() => {
    try { return JSON.parse(localStorage.getItem('userData'))?.tenant_name || null; } catch { return null; }
  })();

  // /client-portal/* — external customer portal for end customers.
  // Backend customer APIs stay on /customer-auth and /portal to avoid route collisions.
  if (currentPath.startsWith('/client-portal')) {
    return (
      <Router basename="/client-portal">
        <ErrorBoundary>
          <Suspense fallback={<ChunkLoadingFallback />}>
            <PortalApp />
          </Suspense>
        </ErrorBoundary>
      </Router>
    );
  }

  // /login — tenant login page
  if (currentPath === '/login') {
    return (
      <Router>
        <ErrorBoundary>
          <Suspense fallback={<ChunkLoadingFallback />}>
            <TenantLoginPage />
          </Suspense>
        </ErrorBoundary>
      </Router>
    );
  }

  // Public legal pages used by signup consent links.
  if (currentPath === '/terms' || currentPath === '/privacy') {
    return (
      <Router>
        <ErrorBoundary>
          <Suspense fallback={<ChunkLoadingFallback />}>
            <LegalPage type={currentPath === '/privacy' ? 'privacy' : 'terms'} />
          </Suspense>
        </ErrorBoundary>
      </Router>
    );
  }

  // /integrations/<slug> — a public page per ERP, prerendered at build time.
  // Not /erp/*: the host proxy routes that prefix to the API.
  const erpPageMatch = currentPath.match(/^\/integrations\/([a-z0-9-]+)$/);
  if (erpPageMatch) {
    return (
      <Router>
        <ErrorBoundary>
          <Suspense fallback={<ChunkLoadingFallback />}>
            <ErpPage slug={erpPageMatch[1]} />
          </Suspense>
        </ErrorBoundary>
      </Router>
    );
  }

  if (currentPath.startsWith('/stock-count/shared/')) {
    return (
      <Router>
        <ErrorBoundary>
          <Suspense fallback={<ChunkLoadingFallback />}>
            <Routes>
              <Route path="/stock-count/shared/:token" element={<StockCountShareView />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </Router>
    );
  }

  if (currentPath.startsWith('/grn-receiving/shared/')) {
    return (
      <Router>
        <ErrorBoundary>
          <Suspense fallback={<ChunkLoadingFallback />}>
            <Routes>
              <Route path="/grn-receiving/shared/:token" element={<GrnReceivingView />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </Router>
    );
  }

  // /branch — the branch staff's own door. Renders outside the tenant shell:
  // its sign-in is a phone and a WhatsApp code, never the office login form,
  // and a shelf does not need the sidebar. The count sheet is reachable from
  // it with the shift token the portal issues.
  if (currentPath === '/branch' || currentPath.startsWith('/branch/')) {
    return (
      <Router>
        <ErrorBoundary>
          <Suspense fallback={<ChunkLoadingFallback />}>
            <Routes>
              <Route path="/branch" element={<BranchPortal />} />
              {/* The guard is a component, so the token is read when the route
                  renders — not when the app first did, which for a phone that
                  signed in on this very page was before there was a token. */}
              <Route path="/branch/count/:sessionId/:branch" element={<RequireStaff><MobileCountView /></RequireStaff>} />
              <Route path="/branch/review/:sessionId/:branch" element={<RequireStaff><BranchReview /></RequireStaff>} />
              <Route path="/branch/*" element={<Navigate to="/branch" replace />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </Router>
    );
  }

  // /supplier/* — public (unauthenticated) supplier RFQ quoting surface.
  // Renders outside the tenant app shell; auth is the supplier's own portal session,
  // never the tenant authToken. No auth guard.
  if (isSupplierPortalPath(currentPath)) {
    return (
      <Router>
        <ErrorBoundary>
          <Suspense fallback={<ChunkLoadingFallback />}>
            <SupplierApp />
          </Suspense>
        </ErrorBoundary>
      </Router>
    );
  }

  // /activate-account — public email activation landing page
  if (currentPath === '/activate-account' || currentPath === '/auth/activate' || currentPath === '/customer-auth/activate') {
    return (
      <Router>
        <ErrorBoundary>
          <Suspense fallback={<ChunkLoadingFallback />}>
            <ActivateAccountPage />
          </Suspense>
        </ErrorBoundary>
      </Router>
    );
  }

  // /reset-password - public password reset landing page
  if (currentPath === '/reset-password') {
    return (
      <Router>
        <ErrorBoundary>
          <Suspense fallback={<ChunkLoadingFallback />}>
            <ResetPasswordPage />
          </Suspense>
        </ErrorBoundary>
      </Router>
    );
  }

  // / — landing page for visitors, tenant home for authenticated users
  if (currentPath === '/') {
    if (isAuthenticated) {
      return (
        <Router>
          <Routes>
            <Route path="*" element={<Navigate to={TENANT_HOME_PATH} replace />} />
          </Routes>
        </Router>
      );
    }
    return (
      <Router>
        <ErrorBoundary>
          <LandingPage
            onLoginClick={() => { window.location.href = '/login'; }}
            onRegisterClick={() => { window.location.href = '/login?mode=register'; }}
          />
        </ErrorBoundary>
      </Router>
    );
  }

  // All other paths: require authentication, redirect to appropriate login
  if (!isAuthenticated) {
    return (
      <Router>
        <Routes>
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    );
  }

  // A phone-session office admin is here for the filing pages only; the
  // desk's own tools — the assistant, feedback — are not part of that job.
  const deskScoped = (() => {
    try { return Array.isArray(JSON.parse(localStorage.getItem('deskScope') || 'null')); }
    catch { return false; }
  })();

  return (
    <Router>
      <RoutePathSync onPathChange={setCurrentPath} />
      <ErrorBoundary>
        <div className={`app ${isAuthenticated ? 'with-sidebar' : ''}`}>
          {isAuthenticated && (
            <Sidebar
              isCollapsed={sidebarCollapsed && !mobileSidebarOpen}
              onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
              isOpen={mobileSidebarOpen}
              onNavigate={() => setMobileSidebarOpen(false)}
              systemHealth={systemHealth}
              enabledModules={effectiveModules}
              moduleMeta={moduleCatalog || {}}
            />
          )}
          {isAuthenticated && mobileSidebarOpen && (
            <button
              type="button"
              className="sidebar-overlay"
              aria-label="Close navigation"
              onClick={() => setMobileSidebarOpen(false)}
            />
          )}

          <div className={`main-wrapper ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
            <header className="app-header">
              <div className="header-content">
                <div className="header-left">
                  {isAuthenticated && (
                    <button
                      type="button"
                      className="hamburger-btn"
                      aria-label="Open navigation"
                      aria-expanded={mobileSidebarOpen}
                      onClick={() => setMobileSidebarOpen(true)}
                    >
                      <Menu size={20} strokeWidth={2.1} />
                    </button>
                  )}
                  {!isAuthenticated && (
                    <h1 className="app-title">Acuon</h1>
                  )}
                  {isAuthenticated && (
                    <div className="page-title">
                      <h2>Acuon Platform</h2>
                      {tenantName && (
                        <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 8, fontWeight: 400 }}>
                          · {tenantName}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {isAuthenticated && <HeaderSearch />}

                <div className="header-right">
                  {isAuthenticated && <ThemeToggle />}
                  {isAuthenticated && !deskScoped && (
                    <button
                      type="button"
                      className="header-assistant-btn"
                      onClick={() => setAssistantOpen(true)}
                      onMouseEnter={loadGlobalAssistant}
                      onFocus={loadGlobalAssistant}
                      title="Open Acuon Assistant"
                    >
                      <Bot size={17} />
                      <span>Assistant</span>
                    </button>
                  )}
                  {isAuthenticated && !deskScoped && <FeedbackWidget />}
                  {isAuthenticated && activeDocumentIntakes.length > 0 && (
                    <button
                      type="button"
                      className="email-intake-status"
                      onClick={() => setShowNotifDropdown(true)}
                      title="View email document processing status"
                    >
                      <LoaderCircle className="email-intake-status-spinner" size={17} />
                      <span className="email-intake-status-copy">
                        <strong>
                          Processing {activeDocumentIntakes.length} email {activeDocumentIntakes.length === 1 ? 'document' : 'documents'}
                        </strong>
                        <small>
                          {activeDocumentIntakes[0]?.metadata?.processing_stage || 'Extracting document data'}
                        </small>
                      </span>
                    </button>
                  )}
                  {isAuthenticated && (
                    <div style={{ position: 'relative', marginRight: 12 }}>
                      <button
                        onClick={() => setShowNotifDropdown(v => !v)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', position: 'relative', padding: '6px', borderRadius: 6, color: '#374151', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                        title="Notifications"
                        aria-label="Notifications"
                      >
                        <Bell size={18} strokeWidth={2} />
                        {notifUnread > 0 && (
                          <span style={{ position: 'absolute', top: 0, right: 0, background: '#ef4444', color: '#fff', borderRadius: '50%', fontSize: 10, fontWeight: 700, minWidth: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
                            {notifUnread > 9 ? '9+' : notifUnread}
                          </span>
                        )}
                      </button>
                      {showNotifDropdown && (
                        <div
                          style={{ position: 'absolute', top: '110%', right: 0, width: 'min(380px, calc(100vw - 24px))', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 8px 30px rgba(0,0,0,0.12)', zIndex: 9999, overflow: 'hidden' }}
                          onClick={e => e.stopPropagation()}
                        >
                          <div style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>Notification center</span>
                            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                              {notifUnread > 0 && <button type="button" onClick={markAllNotifRead} style={{ border: 0, background: 'none', color: '#64748b', fontSize: 12, cursor: 'pointer', padding: 0 }}>Mark all read</button>}
                              <a href="/workflows?tab=notifications" onClick={() => setShowNotifDropdown(false)} style={{ fontSize: 12, color: '#3b82f6', textDecoration: 'none' }}>View all</a>
                            </div>
                          </div>
                          {notifBell.length === 0 ? (
                            <div style={{ padding: '20px 14px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No notifications yet</div>
                          ) : (
                            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                              {notifBell.slice(0, 10).map(n => {
                                const typeColors = { success: '#16a34a', error: '#dc2626', warning: '#d97706', info: '#2563eb', processing: '#2563eb' };
                                const color = typeColors[n.notification_type] || '#64748b';
                                return (
                                  <a key={n.id} href={n.action_path || '/workflows?tab=notifications'} onClick={() => setShowNotifDropdown(false)} style={{ padding: '10px 14px', borderBottom: '1px solid #f8fafc', background: n.is_read ? '#fff' : '#eff6ff', display: 'flex', gap: 10, alignItems: 'flex-start', textDecoration: 'none' }}>
                                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: color, marginTop: 5, flexShrink: 0, opacity: n.is_read ? 0.3 : 1 }} />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b', marginBottom: 2 }}>{n.title || n.workflow_name || 'Notification'}</div>
                                      <div style={{ fontSize: 12, color: '#64748b', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{n.message}</div>
                                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3, display: 'flex', gap: 8 }}>
                                        <span>{n.source === 'documents' ? 'Document intake' : n.source}</span>
                                        <span>{new Date(n.created_at).toLocaleString()}</span>
                                      </div>
                                    </div>
                                  </a>
                                );
                              })}
                            </div>
                          )}
                          <div style={{ padding: '8px 14px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 8 }}>
                            <a href="/settings#notifications" onClick={() => setShowNotifDropdown(false)} style={{ fontSize: 12, color: '#64748b', textDecoration: 'none' }}>Preferences</a>
                          </div>
                        </div>
                      )}
                      {showNotifDropdown && (
                        <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }} onClick={() => setShowNotifDropdown(false)} />
                      )}
                    </div>
                  )}
                  {isAuthenticated ? (
                    <UserProfile user={user} onLogout={handleLogout} />
                  ) : (
                    <div className="auth-section">
                      <button
                        className="btn btn-primary"
                        onClick={() => setShowLogin(true)}
                      >
                        Sign In
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </header>

            <main className={`app-main ${currentPath === '/ai-chat' ? 'ai-chat-main' : ''} ${isFlushPath(currentPath) ? 'flush-main' : ''}`}>
              {isAuthenticated && (
                <Routes>
                  <Route path="/" element={<Navigate to={TENANT_HOME_PATH} replace />} />
                  <Route path="/dashboard" element={<ProtectedRoute element={Dashboard} />} />
                  <Route path="/tasks" element={<ProtectedRoute element={TasksView} moduleKey="tasks" />} />
                  <Route path="/analytics" element={<ProtectedRoute element={AnalyticsView} moduleKey="analytics" />} />
                  <Route path="/ai-insights" element={<ProtectedRoute element={AIInsights} moduleKey="ai_insights" />} />
                  <Route path="/ops-dashboard" element={<ProtectedRoute element={OperationsDashboard} moduleKey="ops_dashboard" />} />
                  <Route path="/operations-control" element={<ProtectedRoute element={OperationsControlCenter} moduleKey="workflows" />} />
                  <Route path="/customer-orders" element={<ProtectedRoute element={CustomerOrdersView} />} />
                  <Route path="/erp-data" element={<ProtectedRoute element={ErpDataView} moduleKey="erp_data" />} />
                  <Route path="/inventory" element={<ProtectedRoute element={InventoryPlanningView} moduleKey="erp_data" />} />
                  <Route path="/ai-recommendations" element={<ProtectedRoute element={AIRecommendationsView} moduleKey="ai_recommendations" />} />
                  <Route path="/task-escalations" element={<ProtectedRoute element={TaskEscalationsView} moduleKey="task_escalations" />} />
                  <Route path="/ai-chat" element={<ProtectedRoute element={AIChatView} />} />
                  <Route path="/canvas" element={<ProtectedRoute element={CanvasView} moduleKey="canvas" />} />
                  <Route path="/tools" element={<ProtectedRoute element={ToolsView} moduleKey="tools" />} />
                  <Route path="/settings" element={<ProtectedRoute element={SettingsView} />} />
                  <Route path="/rfq" element={<ProtectedRoute element={RFQView} moduleKey="rfq" />} />
                  <Route path="/purchase-management" element={<ProtectedRoute element={PRManagementView} moduleKey="rfq" />} />
                  <Route path="/pr-management" element={<Navigate to="/purchase-management" replace />} />
                  <Route path="/finance" element={<ProtectedRoute element={FinanceCommandCenterView} moduleKey="cashflow" />} />
                  <Route path="/ap" element={<ProtectedRoute element={APAgentView} moduleKey="ap" />} />
                  <Route path="/ap-workbench" element={<ProtectedRoute element={APWorkbenchView} moduleKey="ap" />} />
                  <Route path="/finance-action-ledger" element={<ProtectedRoute element={FinanceActionLedgerView} moduleKey="ap" />} />
                  <Route path="/cashflow" element={<ProtectedRoute element={CashFlowView} moduleKey="cashflow" />} />
                  <Route path="/finance-scenarios" element={<ProtectedRoute element={CashFlowScenariosView} moduleKey="cashflow" />} />
                  <Route path="/financial-forecast" element={<ProtectedRoute element={FinancialForecastView} moduleKey="financial_forecast" />} />
                  <Route path="/collections" element={<ProtectedRoute element={CollectionsView} moduleKey="collections" />} />
                  <Route path="/collections-workbench" element={<ProtectedRoute element={CollectionsWorkbenchView} moduleKey="collections" />} />
                  <Route path="/anomaly-detection" element={<ProtectedRoute element={AnomalyDetectionView} moduleKey="anomaly_detection" />} />
                  <Route path="/customer-support" element={<ProtectedRoute element={CustomerSupportView} moduleKey="customer_support" />} />
                  <Route path="/whatsapp-sales" element={<ProtectedRoute element={WhatsAppSalesView} moduleKey="whatsapp_sales" />} />
                  <Route path="/sales-bot" element={<ProtectedRoute element={SalesBotView} moduleKey="sales_bot" />} />
                  <Route path="/communication" element={<ProtectedRoute element={CommunicationAgentView} moduleKey="communication" />} />
                  <Route path="/workflows" element={<ProtectedRoute element={WorkflowsView} moduleKey="workflows" />} />
                  <Route path="/document-processing" element={<ProtectedRoute element={DocumentProcessingView} moduleKey="document_processing" />} />
                  <Route path="/bank-rec" element={<ProtectedRoute element={BankRecView} moduleKey="document_processing" />} />
                  <Route path="/export-compliance" element={<ProtectedRoute element={ExportsComplianceView} moduleKey="export_compliance" />} />
                  <Route path="/stock-count" element={<ProtectedRoute element={StockCountView} moduleKey="stock_count" />} />
                  <Route path="/consignment" element={<ProtectedRoute element={ConsignmentView} moduleKey="consignment" />} />
                  <Route path="/transfers" element={<ProtectedRoute element={TransfersView} moduleKey="transfer_requests" />} />
                  <Route path="/transfer-requests" element={<ProtectedRoute element={TransferRequestsView} moduleKey="transfer_requests" />} />
                  <Route path="/transfer-requests/:requestId" element={<ProtectedRoute element={TransferRequestPage} moduleKey="transfer_requests" />} />
                  <Route path="/transfers/notes/:docRef" element={<ProtectedRoute element={TransferNotePage} moduleKey="transfer_requests" />} />
                  <Route path="/stock-card" element={<ProtectedRoute element={StockCardView} moduleKey="stock_count" />} />
                  <Route path="/price-lists" element={<ProtectedRoute element={PriceListsView} moduleKey="consignment" />} />
                  <Route path="/stock-reports" element={<ProtectedRoute element={StockReportsView} moduleKey="consignment" />} />
                  <Route path="/customer-catalog" element={<ProtectedRoute element={CustomerCatalogView} moduleKey="consignment" />} />
                  <Route path="/employees" element={<ProtectedRoute element={EmployeesView} moduleKey="consignment" />} />
                  <Route path="/upload-log" element={<ProtectedRoute element={UploadLogView} moduleKey="consignment" />} />
                  <Route path="/count-dashboard" element={<ProtectedRoute element={CountDashboardView} moduleKey="stock_count" />} />
                  <Route path="/transfer-dashboard" element={<ProtectedRoute element={TransferDashboardView} moduleKey="transfer_requests" />} />
                  <Route path="/sales-reports" element={<ProtectedRoute element={SalesReportsView} moduleKey="consignment" />} />
                  <Route path="/scanned-sales" element={<ProtectedRoute element={ScannedSalesView} moduleKey="stock_count" />} />
                  <Route path="/routes" element={<ProtectedRoute element={RoutesView} moduleKey="transfer_requests" />} />
                  <Route path="/batches" element={<ProtectedRoute element={BatchesView} moduleKey="stock_count" />} />
                  <Route path="/sap-variances" element={<ProtectedRoute element={SapVarianceView} moduleKey="consignment" requiredRole="manager" />} />
                  <Route path="/production" element={<ProtectedRoute element={ProductionView} moduleKey="stock_count" />} />
                  <Route path="/warehouse-counts" element={<ProtectedRoute element={WarehouseCountsView} moduleKey="export_compliance" />} />
                  <Route path="/stock-count/count/:sessionId/:branch" element={<ProtectedRoute element={MobileCountView} moduleKey="stock_count" />} />
                  <Route path="/master-data" element={<ProtectedRoute element={MasterDataView} moduleKey="document_processing" />} />
                  <Route path="/approvals" element={<ProtectedRoute element={ApprovalsView} moduleKey="stock_count" />} />
                  {/* its old address, from while the host proxy's API rule captured anything starting with "ap" */}
                  <Route path="/count-approvals" element={<Navigate to="/approvals" replace />} />
                  <Route path="/price-book" element={<ProtectedRoute element={PriceBookView} moduleKey="price_book" />} />
                  <Route path="/reports" element={<ProtectedRoute element={ReportsView} moduleKey="document_processing" />} />
                  <Route path="/procurement-control" element={<ProtectedRoute element={ProcurementControlView} moduleKey="ap" />} />
                  <Route path="/cabinet" element={<ProtectedRoute element={CabinetView} moduleKey="document_processing" />} />
                  <Route path="/capture-settings" element={<ProtectedRoute element={CaptureSettingsView} moduleKey="document_processing" requiredRole="admin" />} />
                  <Route path="/capture-activity" element={<ProtectedRoute element={CaptureActivityView} moduleKey="document_processing" requiredRole="manager" />} />
                  <Route path="/erp-settings" element={<ProtectedRoute element={ErpSettingsView} moduleKey="document_processing" requiredRole="admin" />} />
                  <Route path="/search" element={<ProtectedRoute element={SearchView} moduleKey="document_processing" />} />
                  <Route path="/doc-inbox" element={<ProtectedRoute element={TransmittalsInboxView} moduleKey="document_processing" />} />
                  <Route path="/route-templates" element={<ProtectedRoute element={RouteTemplatesView} moduleKey="document_processing" requiredRole="manager" />} />
                  <Route path="/validation-rules" element={<ProtectedRoute element={ValidationRulesView} moduleKey="document_processing" requiredRole="manager" />} />
                  <Route path="/transaction-folders" element={<ProtectedRoute element={WorkspaceFoldersView} moduleKey="document_processing" />} />
                  <Route path="/transaction-folders/:id" element={<ProtectedRoute element={FolderView} moduleKey="document_processing" />} />
                  <Route path="/staged-records" element={<ProtectedRoute element={StagedRecordsView} moduleKey="staged_records" />} />
                  <Route path="/grn-receiving" element={<ProtectedRoute element={GrnReceivingView} moduleKey="document_processing" />} />
                  <Route path="/presentations" element={<ProtectedRoute element={PresentationAgentView} moduleKey="presentations" />} />
                  <Route path="/jobs" element={<ProtectedRoute element={JobSchedulerView} moduleKey="jobs" />} />
                  <Route path="/users" element={<ProtectedRoute element={UserManagementView} />} />
                  <Route path="/token-usage" element={<ProtectedRoute element={TokenUsageView} />} />
                  <Route path="/ar-collections" element={<ProtectedRoute element={ARCollectionsView} moduleKey="collections" />} />
                  <Route path="/ar-analytics" element={<ProtectedRoute element={ARAnalyticsView} moduleKey="collections" />} />
                  <Route path="/ar-disputes" element={<ProtectedRoute element={ARDisputesView} moduleKey="collections" />} />
                  <Route path="/supplier-payments" element={<ProtectedRoute element={SupplierPaymentsView} moduleKey="supplier_payments" />} />
                  <Route path="/modules" element={<ProtectedRoute element={ModulesView} />} />
                  <Route path="/organisation" element={<ProtectedRoute element={TenantManagementView} />} />
                  <Route path="/plan" element={<ProtectedRoute element={PlanUsageView} />} />
                  <Route path="/tenant-onboarding" element={<ProtectedRoute element={TenantOnboardingView} requiredRole="manager" />} />
                </Routes>
              )}
            </main>
          </div>

          {deskScoped ? null : assistantOpen ? (
            <Suspense fallback={null}>
              <GlobalAssistantDock
                open
                onOpen={() => setAssistantOpen(true)}
                onClose={() => setAssistantOpen(false)}
              />
            </Suspense>
          ) : (
            <button
              type="button"
              className="assistant-rail-button"
              onClick={() => setAssistantOpen(true)}
              onMouseEnter={loadGlobalAssistant}
              onFocus={loadGlobalAssistant}
            >
              <Bot size={18} />
              Assistant
            </button>
          )}

          {/* Notifications */}
          <div className="notifications-container">
            {notifications.map(notification => (
              <div key={notification.id} className={`notification notification-${notification.type}`}>
                <div className="notification-header">
                  <h4>{notification.title}</h4>
                  <button
                    className="notification-close"
                    onClick={() => removeNotification(notification.id)}
                  >
                    ×
                  </button>
                </div>
                <p>{notification.message}</p>
                <small>{new Date(notification.timestamp).toLocaleTimeString()}</small>
              </div>
            ))}
          </div>

          {/* Login Modal */}
          {showLogin && (
            <Suspense fallback={null}>
              <LoginForm
                onLoginSuccess={handleLoginSuccess}
                onClose={() => setShowLogin(false)}
              />
            </Suspense>
          )}
        </div>
      </ErrorBoundary>
    </Router>
  );
}

export default App;
